import {Component, ViewChild} from '@angular/core';
import { Table } from 'primeng/table';
import {ConfirmationService, LazyLoadEvent, MessageService} from 'primeng/api';
import {ConfiguratorLandingPageService, DatastoreDto, LandingPageDTO, Partner} from '@tj/tj-dmp-lib-api';
import {Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {AccountService} from '../../../auth/account.service';
import {isAdminOfTenant} from '../../../auth/admin-levels';
import {SelectAffectedPartnersComponent} from '../../../components/select-affected-partners/select-affected-partners.component';

@Component({
    templateUrl: './configuration.component.html',
    styleUrls: ['./configuration.component.scss'],
    standalone: false
})
export class ConfigurationComponent {
    breadcrumb = [
        {label: this.translate.instant('menu.landingpage.label')},
        {label: this.translate.instant('menu.landingpage.configuration')}
    ];
    description = this.translate.instant('landingPage.configuration.pageDescription');

    datastore: DatastoreDto;
    partner: Partner;
    isAdmin = false;
    sharingTarget: LandingPageDTO;

    configs = [];
    totalRecords = 0;

    actualEvent: LazyLoadEvent = {
        first: 0,
        rows: 25
    };

    state: { message: string };

    @ViewChild(SelectAffectedPartnersComponent) affectedPartnersSettings: SelectAffectedPartnersComponent;

    constructor(private configService: ConfiguratorLandingPageService, private confirmationService: ConfirmationService,
                private messageService: MessageService, private router: Router, private readonly translate: TranslateService,
                private accountService: AccountService) {
        const navigation = this.router.getCurrentNavigation();
        const state = navigation?.extras?.state as { message: string } | undefined;
        this.state = state;
        this.translate.onLangChange.subscribe(() => this.rebuildTranslatedHeader());
    }

    private rebuildTranslatedHeader(): void {
        this.breadcrumb = [
            {label: this.translate.instant('menu.landingpage.label')},
            {label: this.translate.instant('menu.landingpage.configuration')}
        ];
        this.description = this.translate.instant('landingPage.configuration.pageDescription');
    }

    ngOnInit(): void {
        if (this.state?.message === 'success_created') {
            setTimeout(() => this.messageService.add({
                severity: 'success',
                summary: this.translate.instant('components.dyn-raw-detail-display.successSummary'),
                detail: this.translate.instant('landingPage.configuration.createdSuccessDetail')
            }));
        } else if (this.state?.message === 'success_updated') {
            setTimeout(() => this.messageService.add({
                severity: 'success',
                summary: this.translate.instant('components.dyn-raw-detail-display.successSummary'),
                detail: this.translate.instant('landingPage.configuration.updatedSuccessDetail')
            }));
        }
    }

    datastoreChange(datastore: DatastoreDto) {
        this.datastore = datastore;
        if (this.datastore) {
            this.accountService.fetch().subscribe((account) => {
                this.isAdmin = isAdminOfTenant(account, this.datastore.code);
            });
        } else {
            this.isAdmin = false;
        }
        this.loadLazy();
    }

    partnerChange(partner: Partner) {
        this.partner = partner;
        this.loadLazy();
    }

    isRecipient(config: LandingPageDTO): boolean {
        return !!(this.partner && config?.partner && config.partner !== this.partner.code);
    }

    openPartnersSetting(config: LandingPageDTO) {
        this.sharingTarget = config;
        this.affectedPartnersSettings.selectedAffectedPartners = config.affectedPartners ? [...config.affectedPartners] : [];
        this.affectedPartnersSettings.openSetting();
    }

    updateAffectedPartners(affectedPartners: string[]) {
        if (!this.sharingTarget?.id) {
            return;
        }
        this.configService.updateLandingPagePartnersUsingPUT(this.datastore.code, this.sharingTarget.id, affectedPartners).subscribe(() => {
            this.messageService.add({
                severity: 'success',
                summary: this.translate.instant('landingPage.configuration.partnersUpdatedSummary'),
                detail: this.translate.instant('landingPage.configuration.partnersUpdatedDetail')
            });
            this.loadLazy();
        });
    }

    duplicateLandingPage(config: LandingPageDTO) {
        if (!config?.id || !this.partner) {
            return;
        }
        this.configService.duplicateLandingPageUsingPOST(this.datastore.code, this.partner.code, config.id).subscribe(() => {
            this.messageService.add({
                severity: 'success',
                summary: this.translate.instant('landingPage.configuration.duplicatedSummary'),
                detail: this.translate.instant('landingPage.configuration.duplicatedDetail')
            });
            this.loadLazy();
        });
    }

    @ViewChild(Table) table: Table;
    search = '';

    onSearch(value: string) {
        this.search = value;
        this.table ? this.table.reset() : this.loadLazy();
    }

    loadLazy(event: LazyLoadEvent = null) {
        if (event) {
            this.actualEvent = event;
        }
        if (!this.datastore || !this.partner) {
            this.configs = [];
            this.totalRecords = 0;
            return;
        }
        const start = Math.round(this.actualEvent.first / this.actualEvent.rows);
        const end = this.actualEvent.rows;
        this.configService.getAllLandingPagesUsingGET(this.datastore.code, start, end, ['id,asc'], this.partner.code, undefined, this.search, 'response').subscribe((res) => {
            this.totalRecords = +res.headers.get('x-total-count');
            this.configs = res.body;
        });
    }

    remove(config: LandingPageDTO) {
        this.confirmationService.confirm({
            message: this.translate.instant('landingPage.configuration.deleteConfirmMessage', {name: config.name}),
            accept: () => {
                this.configService.deleteLandingPageUsingDELETE(this.datastore.code, config.id).subscribe(() => {
                    this.loadLazy();
                    this.messageService.add({
                        severity: 'success',
                        summary: this.translate.instant('components.dyn-raw-detail-display.successSummary'),
                        detail: this.translate.instant('landingPage.configuration.deleteSuccessDetail')
                    });
                }, () => {
                    this.messageService.add({
                        severity: 'error',
                        summary: this.translate.instant('components.dyn-raw-detail-display.errorSummary'),
                        detail: this.translate.instant('landingPage.template.genericErrorDetail')
                    });
                });
            }
        });
    }
}
